import React from "react";
import { CheckCircle2, Loader2, AlertCircle, X } from 'lucide-react';
import { UploadBatchProgress } from '@/hooks/useUploadProgress';
import { Progress } from '@/components/ui/progress';

interface UploadProgressTrackerProps {
  progress: UploadBatchProgress;
  onDismiss?: () => void;
}

export function UploadProgressTracker({ progress, onDismiss }: UploadProgressTrackerProps) {
  const doneCount = progress.completedFiles + progress.failedFiles;

  const statusLabel = progress.isInitializing
    ? 'Preparing upload...'
    : progress.isComplete
      ? progress.hasErrors
        ? `Completed with ${progress.failedFiles} error(s)`
        : 'All files uploaded successfully!'
      : `Uploading ${doneCount}/${progress.totalFiles} files...`;

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {progress.isComplete ? (
            progress.hasErrors ? (
              <AlertCircle className="h-5 w-5 text-destructive" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-success" />
            )
          ) : (
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          )}
          <span className="font-medium text-sm text-foreground">
            {statusLabel}
          </span>
        </div>

        {progress.isComplete && onDismiss && (
          <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Progress value={progress.progress} className="h-2 transition-all duration-500 ease-out" />

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>✅ {progress.completedFiles} uploaded</span>
        {!progress.isInitializing && doneCount < progress.totalFiles && !progress.isComplete && (
          <span>⏳ {progress.totalFiles - doneCount} remaining</span>
        )}
        {progress.failedFiles > 0 && <span>❌ {progress.failedFiles} failed</span>}
      </div>
    </div>
  );
}
